// dashboard/auth/CompleteClinicSignupScreen.js
// Self-serve clinic signup, step 2+3 of docs/CLINIC_SELF_SERVE_ONBOARDING_
// SKETCH.md (2026-09-10, Bryan's explicit direction — fully automatic, no
// verification gate). Reached whenever App.js's hydrateProfile finds a
// valid session with no profiles row at all — the normal state right
// after LoginScreen's signup form calls supabase.auth.signUp(), whether
// that returns a session immediately or only after the person clicks an
// email confirmation link and returns.
//
// Two steps in one component (not two page loads): the person's own name
// + legal acceptance (mirrors SetPasswordScreen.js's "isNewProfile"
// branch exactly, since both create a profiles row via the same
// ensure_current_user_profile RPC), then clinic details. Calls
// ensure_current_user_profile then create_clinic_self_serve sequentially
// on final submit — if the second call fails (validation, or a race where
// this profile already has a clinic from another tab), the profile from
// step 1 is already safely committed and this screen can just be
// retried, matching SetPasswordScreen's own "idempotent profile creation
// first" ordering rationale.

import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { supabase } from '../../supabase';
import { c, CAIRN_GLYPH_PATHS } from '../constants';
import { PRIVACY_POLICY_VERSION, TERMS_VERSION } from '../../legalDocumentVersions';

export default function CompleteClinicSignupScreen() {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [legalAccepted, setLegalAccepted] = useState(false);

  const [clinicName, setClinicName] = useState('');
  const [clinicEmail, setClinicEmail] = useState('');
  const [clinicPhone, setClinicPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [er1Name, setEr1Name] = useState('');
  const [er1Phone, setEr1Phone] = useState('');
  const [er2Name, setEr2Name] = useState('');
  const [er2Phone, setEr2Phone] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function withTimeout(promise, label) {
    return Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error(t('setPassword.timedOut', { label }))), 10000)),
    ]);
  }

  const canSubmitStep1 = firstName.trim() && lastName.trim() && legalAccepted;
  const canSubmitStep2 = clinicName.trim() && clinicEmail.trim();

  const handleStep1Submit = e => {
    e.preventDefault();
    if (!canSubmitStep1) return;
    setStep(2);
  };

  const handleStep2Submit = async e => {
    e.preventDefault();
    if (!canSubmitStep2 || saving) return;
    setSaving(true); setError('');
    try {
      const now = new Date().toISOString();
      // Audit finding #2 (2026-09-10): profile creation and clinic
      // creation used to be two separately-committed RPC calls — a lost
      // connection, closed tab, or failure between them stranded the
      // account with a profile but no clinic and no route back.
      // create_clinic_self_serve (migration 213) now does both
      // atomically in one call — ensure_current_user_profile is called
      // from inside it, in the same transaction, so either everything
      // commits together or none of it does. It also transparently
      // handles finding #5's case (this profile turns out to already
      // have a pending Doctor/Manager invitation elsewhere — e.g. an
      // auth.users account existed with no profile yet, so this screen
      // was reached instead of the invite-link flow): it joins that
      // clinic instead of creating a redundant new one, reported back
      // via `joined_existing_clinic`.
      const { error: ce } = await withTimeout(
        supabase.rpc('create_clinic_self_serve', {
          p_first_name: firstName.trim(),
          p_last_name: lastName.trim(),
          p_tos_accepted_at: now,
          p_privacy_policy_accepted_at: now,
          p_tos_accepted_version: TERMS_VERSION,
          p_privacy_policy_accepted_version: PRIVACY_POLICY_VERSION,
          p_legal_acceptance_locale: document.documentElement.lang || 'en',
          p_clinic_name: clinicName.trim(),
          p_clinic_email: clinicEmail.trim(),
          p_clinic_phone: clinicPhone.trim() || null,
          p_address: address.trim() || null,
          p_city: city.trim() || null,
          p_state: state.trim() || null,
          p_zip: zip.trim() || null,
          p_emergency_hospital_1_name: er1Name.trim() || null,
          p_emergency_hospital_1_phone: er1Phone.trim() || null,
          p_emergency_hospital_2_name: er2Name.trim() || null,
          p_emergency_hospital_2_phone: er2Phone.trim() || null,
        }),
        t('signup.labelCreatingClinic'),
      );
      if (ce) throw ce;

      // Full reload, matching SetPasswordScreen's own convention — re-runs
      // normal auth/profile/clinic_members hydration from a clean slate
      // rather than splicing brand-new state into already-rendered app
      // state.
      window.location.href = window.location.origin;
    } catch (err) {
      const message = err?.message || '';
      if (message.includes('ACCOUNT_ALREADY_HAS_CLINIC')) {
        setError(t('signup.errorAlreadyHasClinic'));
      } else {
        setError(message || t('signup.errorCreateClinic'));
      }
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 14, color: c.slate, boxSizing: 'border-box', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 5 };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: c.fog, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif', padding: '40px 16px' }}>
      <div style={{ backgroundColor: c.white, borderRadius: 20, padding: 40, width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(79,100,115,0.12)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <svg width="28" height="28" viewBox="0 0 128 128">
            {CAIRN_GLYPH_PATHS.map((d, i) => <path key={i} d={d} fill={c.slate} />)}
          </svg>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.slate, letterSpacing: 2 }}>Cairn</div>
            <div style={{ fontSize: 9, color: c.moss, letterSpacing: 2, textTransform: 'uppercase' }}>PET HEALTH</div>
          </div>
        </div>

        {step === 1 ? (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 600, color: c.slate, marginBottom: 4 }}>{t('signup.titleYourInfo')}</h1>
            <p style={{ fontSize: 13, color: c.textSoft, marginBottom: 24 }}>{t('signup.subtitleYourInfo')}</p>
            <form onSubmit={handleStep1Submit}>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="signup-first-name" style={labelStyle}>{t('setPassword.firstName')}</label>
                <input id="signup-first-name" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} autoFocus style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="signup-last-name" style={labelStyle}>{t('setPassword.lastName')}</label>
                <input id="signup-last-name" type="text" value={lastName} onChange={e => setLastName(e.target.value)} style={inputStyle} />
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: c.textSoft, marginBottom: 20 }}>
                <input type="checkbox" checked={legalAccepted} onChange={e => setLegalAccepted(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  {t('setPassword.legalAcceptPrefix')}{' '}
                  <a href="https://cairnpethealth.com/terms" target="_blank" rel="noreferrer">{t('setPassword.terms')}</a>
                  {' '}{t('setPassword.legalAcceptMiddle')}{' '}
                  <a href="https://cairnpethealth.com/privacy" target="_blank" rel="noreferrer">{t('setPassword.privacy')}</a>.
                </span>
              </label>
              <button type="submit" disabled={!canSubmitStep1}
                style={{ width: '100%', padding: '12px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: canSubmitStep1 ? 1 : 0.5 }}>
                {t('signup.continue')}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 600, color: c.slate, marginBottom: 4 }}>{t('signup.titleClinicInfo')}</h1>
            <p style={{ fontSize: 13, color: c.textSoft, marginBottom: 24 }}>{t('signup.subtitleClinicInfo')}</p>
            <form onSubmit={handleStep2Submit}>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="signup-clinic-name" style={labelStyle}>{t('signup.clinicName')}</label>
                <input id="signup-clinic-name" type="text" value={clinicName} onChange={e => setClinicName(e.target.value)} autoFocus style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="signup-clinic-email" style={labelStyle}>{t('signup.clinicEmail')}</label>
                <input id="signup-clinic-email" type="email" value={clinicEmail} onChange={e => setClinicEmail(e.target.value)} placeholder={t('signup.clinicEmailPlaceholder')} style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="signup-clinic-phone" style={labelStyle}>{t('signup.clinicPhone')}</label>
                <input id="signup-clinic-phone" type="tel" value={clinicPhone} onChange={e => setClinicPhone(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="signup-address" style={labelStyle}>{t('signup.address')}</label>
                <input id="signup-address" type="text" value={address} onChange={e => setAddress(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px', gap: 8, marginBottom: 20 }}>
                <div>
                  <label htmlFor="signup-city" style={labelStyle}>{t('signup.city')}</label>
                  <input id="signup-city" type="text" value={city} onChange={e => setCity(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label htmlFor="signup-state" style={labelStyle}>{t('signup.state')}</label>
                  <input id="signup-state" type="text" value={state} onChange={e => setState(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label htmlFor="signup-zip" style={labelStyle}>{t('signup.zip')}</label>
                  <input id="signup-zip" type="text" value={zip} onChange={e => setZip(e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${c.border}`, paddingTop: 16, marginBottom: 8 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: c.slate, margin: '0 0 4px' }}>{t('signup.erHospitalsTitle')}</p>
                <p style={{ fontSize: 11, color: c.textSoft, margin: '0 0 12px', lineHeight: 1.4 }}>{t('signup.erHospitalsHint')}</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div>
                  <label htmlFor="signup-er1-name" style={labelStyle}>{t('signup.erHospitalName', { n: 1 })}</label>
                  <input id="signup-er1-name" type="text" value={er1Name} onChange={e => setEr1Name(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label htmlFor="signup-er1-phone" style={labelStyle}>{t('signup.erHospitalPhone', { n: 1 })}</label>
                  <input id="signup-er1-phone" type="tel" value={er1Phone} onChange={e => setEr1Phone(e.target.value)} style={inputStyle} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
                <div>
                  <label htmlFor="signup-er2-name" style={labelStyle}>{t('signup.erHospitalName', { n: 2 })}</label>
                  <input id="signup-er2-name" type="text" value={er2Name} onChange={e => setEr2Name(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label htmlFor="signup-er2-phone" style={labelStyle}>{t('signup.erHospitalPhone', { n: 2 })}</label>
                  <input id="signup-er2-phone" type="tel" value={er2Phone} onChange={e => setEr2Phone(e.target.value)} style={inputStyle} />
                </div>
              </div>

              {error && <p role="alert" style={{ fontSize: 12, color: c.error, marginBottom: 10 }}>{error}</p>}
              <button type="submit" disabled={!canSubmitStep2 || saving}
                style={{ width: '100%', padding: '12px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: (!canSubmitStep2 || saving) ? 0.5 : 1 }}>
                {saving ? t('signup.creating') : t('signup.createClinic')}
              </button>
              <button type="button" onClick={() => setStep(1)} disabled={saving}
                style={{ display: 'block', margin: '14px auto 0', background: 'none', border: 'none', color: c.slate, fontSize: 12, textDecoration: 'underline', cursor: saving ? 'default' : 'pointer' }}>
                {t('signup.back')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
